import { ACCOUNT_RECORDS, checkHasPermission } from '@/middlewares/permissions';
import ApiFactory from '../factories/ApiFactory';
import { prepAutocompleteData, serialize } from '@/services/UtilityService';

const getInvoices = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`posweb/v1/finance/invoices?${query}`).then(
			({ data }) => {
				const invoices = data.data.map((i) => {
					return {
						...i,
						restaurantVat: i.restaurant['vat'],
						restaurantTotalPercentage:
							i.restaurant['baboonPercentage'] +
							i.restaurant['courierPercentage'] +
							i.restaurant['marketingPercentage'],
						subtotal: i['amountNoVatCents'] / 100,
						vat: i['vatCents'] / 100,
						total: (i['vatCents'] + i['amountNoVatCents']) / 100,
						paid: i['paidAmountCents'] / 100,
						unpaid: (i['vatCents'] + i['amountNoVatCents'] - i['paidAmountCents']) / 100,
					};
				});
				resolve(invoices);
			},
			(error) => reject(error)
		);
	});
};

const getPurchases = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`posweb/v1/finance/purchases?${query}`).then(({ data }) => {
			const purchases = data.data.map((i) => {
				return {
					...i,
					restaurantVat: i.business?.['vat'],
					subtotal: i['amountNoVatCents'] / 100,
					vat: i['vatCents'] / 100,
					total: (i['vatCents'] + i['amountNoVatCents']) / 100,
					paid: i['paidAmountCents'] / 100,
					unpaid: (i['vatCents'] + i['amountNoVatCents'] - i['paidAmountCents']) / 100,
				};
			});
			resolve(purchases);
		}, reject);
	});
};

const getStatistics = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`reports/v1/admin?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getItems = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`accounting/v1/invoices/items`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'description', 'id'));
		}, reject);
	});
};

const calcItem = (id, body) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`accounting/v1/invoices/restaurants/${id}/calc-item`, body).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const createInvoice = (body) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`accounting/v1/invoices`, body).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updateInvoice = (id, body) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`accounting/v1/invoices/${id}`, body).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getPercentages = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`systemadmin/v1/restaurants?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const updatePercentages = (payload) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`systemadmin/v1/restaurants/${payload.restaurantID}/finance-data`, payload).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getPaymentTypes = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`accounting/v1/payments/units`).then(({ data }) => {
			resolve(prepAutocompleteData(data.data, 'account', 'id'));
		}, reject);
	});
};

const createPayment = (data) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`accounting/v1/payments`, data).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getInvoiceDetails = (id) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`posweb/v1/finance/invoices/${id}`).then(({ data }) => {
			const invoice = data.data;
			invoice.restaurant.value = invoice.restaurant['restaurantID'];
			invoice.restaurant.label = invoice.restaurant['restaurantName'];
			resolve(invoice);
		}, reject);
	});
};

const getInvoicePayments = (invoiceId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`posweb/v1/finance/payments?invoiceId=${invoiceId}&isFullBody=true`).then(({ data }) => {
			resolve(data.data.sort((a, b) => a.id - b.id));
		}, reject);
	});
};

const addItemToInvoice = (item, invoiceId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`accounting/v1/invoices/${invoiceId}/add-item`, item).then(resolve, reject);
	});
};

const getPayments = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize({ ...filters, isFullBody: true });
		ApiFactory.get(`posweb/v1/finance/payments?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const getDashboardInfo = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize({ ...filters, isFullBody: true });
		ApiFactory.get(`posweb/v1/finance/dashboard?${query}`).then(({ data }) => {
			resolve(data.data);
		}, reject);
	});
};

const canExportEmployeeList = () => {
	return checkHasPermission([ACCOUNT_RECORDS]);
};

export default {
	getInvoices,
	getPurchases,
	getStatistics,
	getItems,
	calcItem,
	createInvoice,
	updateInvoice,
	getPercentages,
	updatePercentages,
	getPaymentTypes,
	createPayment,
	getInvoiceDetails,
	getInvoicePayments,
	addItemToInvoice,
	getPayments,
	getDashboardInfo,
	canExportEmployeeList,
};
